import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";

import { EditIcon } from "../icons";
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EllipsisTooltipText from "./EllipsisTooltipText";
import ActionCheckBox from "../../pages-new/Studio/ActionCheckBox";
import { StatusColors, StatusOptions } from "../../pages-new/Studio/constants";
import { useUpdateWaitTimeMutation, useUpdateWaitTypeMutation, useUpdateFlowMutation, useSetStepMileStone, useRemoveStepMilestone } from "../../useQuery";
import { useNotification } from "../../hooks";
import { Check, RemoveRedEye } from "@mui/icons-material";

const TestTableView = ({
  testCaseResults,
  handleEdit,
  tsrData,
  isCheckbox,
  handleCheckboxChange,
  checkedItems,
  onInlineTestCaseView,
}) => {
  const notify = useNotification();
  const [editingWaitTime, setEditingWaitTime] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [milestoneDescription, setMilestoneDescription] = useState("");

  const { mutate: updateWaitTime } = useUpdateWaitTimeMutation({
    onSuccess: () => {
      notify("Wait time updated successfully", "success");
      setEditingWaitTime(null);
    },
    onError: (error) => {
      notify(`Error updating wait time: ${error.message}`, "error");
    },
  });

  const { mutate: updateWaitType } = useUpdateWaitTypeMutation({
    onSuccess: () => {
      notify("Wait type updated successfully", "success");
    },
    onError: (error) => {
      notify(`Error updating wait type: ${error.message}`, "error");
    },
  });

  const { mutate: updateFlow } = useUpdateFlowMutation({
    onSuccess: () => {
      notify("Flow status updated successfully", "success");
    },
    onError: (error) => {
      notify(`Error updating flow status: ${error.message}`, "error");
    },
  });

  const { mutate: setMilestone } = useSetStepMileStone({
    onSuccess: () => {
      notify("Milestone added successfully", "success");
      handleMilestoneClose();
    },
    onError: (error) => {
      notify(`Error adding milestone: ${error.message}`, "error");
    }
  });

  const { mutate: removeMilestone } = useRemoveStepMilestone({
    onSuccess: () => {
      notify("Milestone removed successfully", "success");
    },
    onError: (error) => {
      notify(`Error removing milestone: ${error.message}`, "error");
    }
  });

  const handleWaitTimeChange = (item, value) => {
    if (value > 999) {
      notify("Maximum wait time allowed is 999", "error");

      value = 999;
    }

    setEditingWaitTime(item.idtest_step);
    setEditedValues({
      ...editedValues,
      [item.idtest_step]: {
        ...(editedValues[item.idtest_step] || item),
        waittime: value,
      },
    });
  };

  const handleWaitTypeChange = (item, value) => {
    updateWaitType({
      teststepid: item.idtest_step,
      tcid: item.Test_Case_Id,
      before: value,
    });
    setEditedValues({
      ...editedValues,
      [item.idtest_step]: {
        ...(editedValues[item.idtest_step] || item),
        wait: value,
      },
    });
  };

  const handleSaveWaitTime = (item) => {
    const editedItem = editedValues[item.idtest_step];
    if (editedItem?.waittime !== item.waittime) {
      updateWaitTime({
        tsid: item.idtest_step,
        tcid: item.Test_Case_Id,
        time: editedItem.waittime,
      });
    }
  };

  const handleFlowChange = (item, newFlow) => {
    updateFlow({
      teststepid: item.idtest_step,
      tcid: item.Test_Case_Id,
      flow: newFlow,
    });
  };

  const handleMilestoneClick = (item) => {
    if (item.ismilestone === "true") {
      removeMilestone(item.idtest_step);
      return;
    }

    setSelectedStep(item);
    setMilestoneDialogOpen(true);
  };

  const handleMilestoneSave = () => {
    if (!milestoneDescription.trim()) {
      notify("Please enter a milestone description", "warning");
      return;
    }

    setMilestone({
      data: {
        testStepId: selectedStep.idtest_step,
        testCaseId: selectedStep.Test_Case_Id,
        milestoneDescription: milestoneDescription.trim()
      }
    });
  };

  const handleMilestoneClose = () => {
    setMilestoneDescription("");
    setSelectedStep(null);
    setMilestoneDialogOpen(false);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ mt: 3, boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {isCheckbox && <TableCell padding="checkbox" />}
              <TableCell>Step Number</TableCell>
              {/* <TableCell>Status</TableCell> */}
              {/* <TableCell>Screenshot</TableCell> */}
              <TableCell>Page Description</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Test Data</TableCell>
              <TableCell>Flow</TableCell>
              <TableCell>Wait Time</TableCell>
              <TableCell>Milestone</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testCaseResults?.data.map((item, index) => (
              <TableRow
                key={item.idtest_step}
                sx={{ backgroundColor: StatusColors[item.Flow] || "#ffffff" }}
              >
                {isCheckbox && (
                  <TableCell padding="checkbox">
                    <ActionCheckBox
                      handleCheckboxChange={handleCheckboxChange}
                      item={item}
                      checkedItems={checkedItems}
                    />
                  </TableCell>
                )}
                <TableCell>{item.Step_Number}</TableCell>
                {/* <TableCell>
                  <Box display="flex" alignItems="center">
                    {item.Issues ? <CircleWarningIcon /> : <CircleTickIcon />}
                  </Box>
                </TableCell> */}
                {/* <TableCell>
                  <Box
                    sx={{
                      width: 100,
                      height: 60,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    {tsrData &&
                    (tsrData[index]?.screenshot1 || tsrData[index]?.Failure_Screenshot_Location) ? (
                      <img
                        style={{ height: "100%", width: "100%", objectFit: "cover" }}
                        src={tsrData[index].screenshot1 || tsrData[index].Failure_Screenshot_Location}
                        alt="testImage"
                      />
                    ) : (
                      <ImageNotSupportedOutlinedIcon sx={{ color: "#8089A8" }} />
                    )}
                  </Box>
                </TableCell> */}
                <TableCell>
                  <Typography>{item.Page_Description || "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{item.Action}</Typography>
                </TableCell>
                <TableCell>
                  <EllipsisTooltipText text={item.TestData || "-"} maxChars={50} mode="chars" />
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={item.Flow || ""}
                    onChange={(e) => handleFlowChange(item, e.target.value)}
                    sx={{ minWidth: "120px" }}
                  >
                    {StatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{
                        min: 0,
                        max: 999,
                      }}
                      value={
                        editingWaitTime === item.idtest_step
                          ? editedValues[item.idtest_step]?.waittime
                          : item.waittime || ""
                      }
                      onChange={(e) => handleWaitTimeChange(item, e.target.value)}
                      sx={{ width: "80px" }}
                    />
                    <Tooltip title={item.wait}>
                      <Select
                        size="small"
                        value={item.wait || "Before"}
                        onChange={(e) => handleWaitTypeChange(item, e.target.value)}
                        sx={{ width: "120px" }}
                      >
                        <MenuItem value="Before">Wait Before</MenuItem>
                        <MenuItem value="After">Wait After</MenuItem>
                        <MenuItem value="WaitTill">Wait Till Found</MenuItem>
                        <MenuItem value="WaitForAllApi">Wait For All API</MenuItem>
                        <MenuItem value="WaitForConfiguredApis">Wait For Configured APIs</MenuItem>
                      </Select>
                    </Tooltip>
                    {editingWaitTime === item.idtest_step && (
                      <IconButton
                        size="small"
                        onClick={() => handleSaveWaitTime(item)}
                        sx={{
                          border: "2px solid #547de8",
                          borderRadius: "88px",
                          width: 30,
                          height: 30,
                        }}
                      >
                        <Check />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {item.ismilestone === "true" && (
                    <Tooltip title={item.milestone_description || "Milestone"}>
                      <Chip
                        icon={<FlagIcon />}
                        label="Milestone"
                        size="small"
                        color="primary"
                        sx={{ 
                          backgroundColor: "#547de8",
                          '& .MuiChip-icon': { color: 'white' }
                        }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() => handleEdit(item)}
                      sx={{
                        border: "2px solid #547de8",
                        borderRadius: "88px",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    {item.ismilestone === "true" ? (
                      <Tooltip title="Remove Milestone">
                        <IconButton
                          onClick={() => handleMilestoneClick(item)}
                          sx={{
                            border: "2px solid #ff5336",
                            borderRadius: "88px",
                            width: 40,
                            height: 40,
                            backgroundColor: "transparent",
                            '&:hover': {
                              backgroundColor: "rgba(255, 83, 54, 0.1)",
                            }
                          }}
                        >
                          <RemoveCircleOutlineIcon sx={{ color: "#ff5336" }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Add Milestone">
                        <IconButton
                          onClick={() => handleMilestoneClick(item)}
                          sx={{
                            border: "2px solid #547de8",
                            borderRadius: "88px",
                            width: 40,
                            height: 40,
                            backgroundColor: "transparent",
                            '&:hover': {
                              backgroundColor: "rgba(84, 125, 232, 0.1)",
                            }
                          }}
                        >
                          <FlagOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                  {item.Keyword === "Inline" && (
                    <Tooltip title="View Inline Test Case">
                      <IconButton
                        onClick={() => onInlineTestCaseView(item.TestData)}
                        sx={{
                          border: "2px solid #547de8",
                          borderRadius: "88px",
                          width: 40,
                          height: 40,
                        }}
                      >
                        <RemoveRedEye />
                      </IconButton>
                    </Tooltip>
                  )}
                    
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Milestone Dialog */}
      <Dialog open={milestoneDialogOpen} onClose={handleMilestoneClose}>
        <DialogTitle>Add Milestone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Milestone Description"
            fullWidth
            variant="outlined"
            value={milestoneDescription}
            onChange={(e) => setMilestoneDescription(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMilestoneClose} sx={{ color: "#ff5336" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleMilestoneSave} 
            variant="contained" 
            sx={{ background: "#ff5336" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestTableView;
